import { useState, useEffect } from "react";
import { api, errMessage } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-collection field schema. Each field is either "input" or "textarea".
 * Critical fields (server-side flag of same name) trigger an in-form warning
 * before save. The server records every change in the audit log regardless.
 */
const SCHEMAS = {
  credentials: {
    title: "Edit credential",
    fields: [
      { key: "holder_name", label: "Full name" },
      { key: "holder_email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "country", label: "Country" },
      { key: "credential_type", label: "Credential type", critical: true },
      { key: "status", label: "Status", critical: true },
      { key: "issue_date", label: "Issue date", critical: true, type: "date" },
      { key: "expiry_date", label: "Expiration date", critical: true, type: "date" },
      { key: "credential_number", label: "Credential number", critical: true },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  licenses: {
    title: "Edit license",
    fields: [
      { key: "holder_name", label: "Holder name" },
      { key: "jurisdiction", label: "Jurisdiction" },
      { key: "status", label: "Status", critical: true },
      { key: "issued_on", label: "Issue date", critical: true, type: "date" },
      { key: "expires_on", label: "Expiration date", critical: true, type: "date" },
      { key: "license_number", label: "License number", critical: true },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  schools: {
    title: "Edit institution",
    fields: [
      { key: "name", label: "Institution name" },
      { key: "legal_name", label: "Legal name" },
      { key: "address", label: "Address" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "country", label: "Country" },
      { key: "website", label: "Website" },
      { key: "accreditation_type", label: "Accreditation scope", critical: true },
      { key: "accreditation_status", label: "Status", critical: true },
      { key: "accredited_at", label: "Issue date", critical: true, type: "date" },
      { key: "expires_at", label: "Expiration date", critical: true, type: "date" },
      { key: "accreditation_number", label: "Accreditation number", critical: true },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  school_applications: {
    title: "Edit school application",
    fields: [
      { key: "name", label: "Institution name" },
      { key: "contact_email", label: "Contact email" },
      { key: "country", label: "Country" },
      { key: "state", label: "State" },
      { key: "city", label: "City" },
      { key: "status", label: "Status", critical: true },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  applications: {
    title: "Edit application",
    fields: [
      { key: "credential_type", label: "Credential type", critical: true },
      { key: "status", label: "Status", critical: true },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
};

export function EditRecordDrawer({ collection, record, open, onClose, onSaved }) {
  const schema = SCHEMAS[collection];
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) {
      const next = {};
      schema?.fields.forEach((f) => {
        let v = record[f.key];
        if (f.type === "date" && v) v = String(v).slice(0, 10);
        next[f.key] = v ?? "";
      });
      setForm(next);
    }
  }, [record, schema]);

  if (!schema) return null;

  const dirty = (k) => record && form[k] !== (record[k] ?? "");
  const dirtyCritical = schema.fields.filter((f) => f.critical && dirty(f.key));

  const save = async () => {
    setSaving(true);
    try {
      const changed = {};
      schema.fields.forEach((f) => {
        if (dirty(f.key)) changed[f.key] = form[f.key];
      });
      if (!Object.keys(changed).length) {
        toast.info("No changes to save");
        return;
      }
      const { data } = await api.patch(`/admin/records/${collection}/${record.id}`, { fields: changed });
      toast.success("Record updated");
      onSaved?.(data.record);
      onClose();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid={`edit-${collection}`}>
        <SheetHeader>
          <SheetTitle>{schema.title}</SheetTitle>
          <SheetDescription>All changes are recorded in the audit log.</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 my-4">
          {schema.fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={f.key} className="text-xs flex items-center gap-2">
                {f.label}
                {f.critical && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                    Critical
                  </span>
                )}
              </Label>
              {f.type === "textarea" ? (
                <Textarea
                  id={f.key}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`field-${f.key}`}
                />
              ) : (
                <Input
                  id={f.key}
                  type={f.type === "date" ? "date" : "text"}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`field-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
        {dirtyCritical.length > 0 && (
          <Alert className="border-amber-300 bg-amber-50" data-testid="critical-warning">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-900 text-sm">
              You are modifying {dirtyCritical.length} critical field(s): {dirtyCritical.map((f) => f.label).join(", ")}.
              These changes are logged for compliance.
            </AlertDescription>
          </Alert>
        )}
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
